import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { globalStyles } from "./styles/globalStyles";
import initTheme from "./styles/themeProvider";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import API_BASE from "./services/apiConfig";
import axios from "axios";
import appConfig from "./config/appConfig";

/* ========= ADMIN ========= */
import ClinicAdminLayout from "./pages/ClinicAdminLayout";
import DashboardHome from "./pages/DashboardHome";
import DoctorsManagementPage from "./pages/DoctorsManagementPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import RevenuePage from "./pages/RevenuePage";
import SubscriptionInvoicesPage from "./pages/SubscriptionInvoicesPage";
import PatientInvoicesPage from "./pages/PatientInvoicesPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import PatientsPage from "./pages/PatientsPage";
import ReportsPage from "./pages/ReportsPage";
import UpiPaymentRedirectPage from "./pages/UpiPaymentRedirectPage";

/* ========= DOCTOR ========= */
import DoctorBookings from "./pages/doctor/DoctorBookings";
import DoctorQueue from "./pages/doctor/DoctorQueue";

/* ========= OPD MODULE ========= */
import DoctorOpdLayout from "./components/DoctorOpdLayout";
import DoctorOpdOpenBookings from "./pages/doctor/DoctorOpdOpenBookings";
import DoctorOpdManager from "./pages/doctor/DoctorOpdManager";
import DoctorOpdSessionDashboard from "./pages/doctor/DoctorOpdSessionDashboard";
import PrescriptionView from "./pages/PrescriptionView";
import PrescriptionPreview from "./pages/PrescriptionPreview";

/* ========= QUEUE DISPLAY ========= */
import QueueDisplayScreen from "./pages/QueueDisplayScreen";

/* ========= PORTAL ========= */
import AppPortal from "./pages/AppPortal";
import ClinicPortalRefreshLoader from "./components/ClinicPortalRefreshLoader";

const AUTH_APP_URL = appConfig.authAppUrl;
const AUTH_HOST_STORAGE_KEY = "clinicAuthOrigin";

function normalizeAuthUrl(target) {
  if (!target) return "";
  return target.replace(/\/(signin|signup)\/?$/i, "").replace(/\/$/, "");
}

function resolveAuthHost(defaultHost) {
  const envDefault = appConfig.authAppUrl;

  if (typeof window === "undefined") {
    return normalizeAuthUrl(defaultHost || envDefault);
  }

  const saved = window.localStorage.getItem(AUTH_HOST_STORAGE_KEY);
  const host = normalizeAuthUrl(AUTH_APP_URL || saved || defaultHost || envDefault || window.location.origin);
  return host || window.location.origin;
}

function RedirectToAuth({ target }) {
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.location.replace(target);
    }
  }, [target]);
  return null;
}

function AuthGuard({ children }) {
  const { auth } = useAuth();
  const [refreshLoaderMinimumComplete, setRefreshLoaderMinimumComplete] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setRefreshLoaderMinimumComplete(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  if (auth?.loading || !refreshLoaderMinimumComplete) {
    return <ClinicPortalRefreshLoader />;
  }

  if (auth?.authenticated && auth?.clinic?.id) {
    return children;
  }

  return <Navigate to="/" replace />;
}

// 🔥 WRAPPER: Provides SubscriptionContext - gets clinicId from localStorage
function AdminDashboardWrapper() {
  return (
    <SubscriptionProvider>
      <ClinicAdminLayout />
    </SubscriptionProvider>
  );
}

function App() {
  useEffect(() => {
    // Initialize and inject theme variables, then global styles
    initTheme();
    const style = document.createElement('style');
    style.id = 'cq-global-styles';
    style.textContent = globalStyles;
    document.head.appendChild(style);
    return () => {
      // clean up global styles but keep theme vars (persist during SPA lifetime)
      const s = document.getElementById('cq-global-styles');
      if (s) document.head.removeChild(s);
    };
  }, []);

  useEffect(() => {
    // In development, rewrite any API requests to the configured backend base URL.
    try {
      const base = API_BASE.replace(/\/$/, '');
      axios.defaults.baseURL = base;
      axios.defaults.withCredentials = true;

      const resolveApiUrl = (url) => {
        if (!url) return url;
        const normalizedBase = base.replace(/\/$/, '');

        if (typeof url === 'string') {
          if (/^https?:\/\//i.test(url)) {
            try {
              const parsed = new URL(url);
              if (parsed.origin === window.location.origin) {
                return `${normalizedBase}${parsed.pathname}${parsed.search}`;
              }
            } catch {}
          }
          if (url.startsWith('/api/')) {
            if (normalizedBase.endsWith('/api')) {
              return `${normalizedBase}${url.slice(4)}`;
            }
            return `${normalizedBase}${url}`;
          }
        }
        return url;
      };

      axios.interceptors.request.use((config) => {
        if (config && config.url) {
          config.url = resolveApiUrl(config.url);
        }
        if (!config.withCredentials) {
          config.withCredentials = true;
        }
        return config;
      });

      if (typeof window !== 'undefined' && window.fetch) {
        const _fetch = window.fetch.bind(window);
        window.fetch = (input, init = {}) => {
          try {
            let url = typeof input === 'string' ? input : input.url;
            const resolvedUrl = resolveApiUrl(url);
            const requestInit = { credentials: 'include', ...init };

            if (typeof input === 'string') {
              return _fetch(resolvedUrl, requestInit);
            }

            const requestOptions = {
              method: input.method,
              headers: input.headers,
              body: input.body,
              mode: input.mode,
              credentials: requestInit.credentials,
              cache: input.cache,
              redirect: input.redirect,
              referrer: input.referrer,
              referrerPolicy: input.referrerPolicy,
              integrity: input.integrity,
              keepalive: input.keepalive,
              signal: input.signal,
            };
            const newReq = new Request(resolvedUrl, requestOptions);
            return _fetch(newReq, requestInit);
          } catch (e) {
            // ignore and fallthrough
          }
          return _fetch(input, init);
        };
      }
    } catch (e) {
      // ignore in production
    }
  }, []);

  // 🔥 CRITICAL: Clear stale subscription data from localStorage on app startup
  // This ensures fresh data is always fetched from backend via SubscriptionContext
  useEffect(() => {
    const clearStaleCache = () => {
      localStorage.removeItem("subscriptionStatus");
      localStorage.removeItem("isActive");
      console.log("✅ Cleared stale subscription cache from localStorage");
    };
    clearStaleCache();
  }, []);

  return (
    <Router>
      <AuthProvider>
      <Routes>

        {/* ================= APP PORTAL ================= */}
        <Route path="/" element={<AppPortal />} />
        <Route path="/app" element={<AppPortal />} />
        <Route path="/pay/upi/:appointmentId" element={<UpiPaymentRedirectPage />} />

        {/* ================= LOGIN ================= */}
        <Route
          path="/login"
          element={<RedirectToAuth target={`${resolveAuthHost()}/signin`} />}
        />
        <Route
          path="/signin"
          element={<RedirectToAuth target={`${resolveAuthHost()}/signin`} />}
        />
        <Route
          path="/signup"
          element={<RedirectToAuth target={`${resolveAuthHost()}/signup`} />}
        />

        {/* ================= ADMIN PANEL ================= */}
        <Route path="/admin-dashboard" element={<AuthGuard><AdminDashboardWrapper /></AuthGuard>}>

          {/* DASHBOARD */}
          <Route index element={<DashboardHome />} />

          {/* CORE MODULES */}
          <Route path="doctors" element={<DoctorsManagementPage />} />
          <Route path="doctors-managementpage" element={<DoctorsManagementPage />} />
          <Route path="appointments" element={<AppointmentsPage />} />
          <Route path="patients" element={<PatientsPage />} />
          <Route path="patients/:patientId/reports" element={<ReportsPage />} />

          {/* BILLING */}
          <Route path="subscription" element={<SubscriptionPage />} />
          <Route path="revenue" element={<RevenuePage />} />
          <Route path="invoices" element={<SubscriptionInvoicesPage />} />
          <Route path="patient-invoices" element={<PatientInvoicesPage />} />

          {/* SETTINGS */}
          <Route path="settings" element={<AdminSettingsPage />} />

          {/* ✅ LIVE QUEUE (FIXED) */}
          <Route path="queue-display" element={<QueueDisplayScreen />} />
          <Route path="queue-display/:scheduleId" element={<QueueDisplayScreen />} />

          {/* ================= DOCTOR OPD MODULE ================= */}
          <Route path="doctor-opd/:doctorId" element={<DoctorOpdLayout />}>
            {/* OPEN SESSION */}
            <Route index element={<DoctorOpdOpenBookings />} />
            {/* SESSION MANAGER */}
            <Route path="manage" element={<DoctorOpdManager />} />
            {/* SESSION DASHBOARD */}
            <Route path="session/:scheduleId" element={<DoctorOpdSessionDashboard />} />
          </Route>

        </Route>

        {/* ================= PRESCRIPTION VIEW ================= */}
        <Route path="/prescription-view/:id" element={<PrescriptionView />} />
        {/* ================= PRESCRIPTION PREVIEW (DEV) ================= */}
        <Route path="/prescription-preview" element={<PrescriptionPreview />} />

        {/* ================= LEGACY DOCTOR ================= */}
        <Route
          path="/doctor-bookings/:scheduleId"
          element={<DoctorBookings />}
        />

        <Route
          path="/doctor-queue/:scheduleId"
          element={<DoctorQueue />}
        />

      </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;